/**
 * cacheService.js
 * 
 * Servizio per la gestione della cache dei dataset
 * Implementa funzioni di salvataggio, recupero e invalidazione della cache
 * Utilizza IndexedDB per persistenza e localStorage per metadati
 */

import { functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';

// Costanti per la configurazione della cache
const CACHE_VERSION = '1.0';
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 ore di default
const CACHE_PREFIX = 'cdg_dataset_';
const META_CACHE_KEY = 'cdg_cache_metadata';
const INDEXED_DB_NAME = 'CDGDatasetCache';
const INDEXED_DB_VERSION = 1;
const STORE_NAME = 'datasets';

// Flag di inizializzazione
let isInitialized = false;
let db = null;

/**
 * Inizializza IndexedDB
 * @returns {Promise<IDBDatabase>}
 */
const initIndexedDB = async () => {
  if (isInitialized && db) return db;
  
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(INDEXED_DB_NAME, INDEXED_DB_VERSION);
    
    request.onerror = (event) => {
      console.error('Errore apertura IndexedDB:', event);
      reject(new Error('Impossibile aprire IndexedDB'));
    };
    
    request.onsuccess = (event) => {
      db = event.target.result;
      isInitialized = true;
      console.log('IndexedDB inizializzato');
      resolve(db);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Crea object store per i dataset se non esiste
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
        console.log('Object store creato:', STORE_NAME);
      }
    };
  });
};

/**
 * Ottiene i metadati della cache
 * @returns {Object} Metadati della cache
 */
const getCacheMetadata = () => {
  try {
    const metaStr = localStorage.getItem(META_CACHE_KEY);
    if (!metaStr) return { version: CACHE_VERSION, entries: {}, lastCleanup: Date.now() };
    return JSON.parse(metaStr);
  } catch (error) {
    console.error('Errore nel recupero metadati cache:', error);
    return { version: CACHE_VERSION, entries: {}, lastCleanup: Date.now() };
  }
};

/**
 * Salva i metadati della cache
 * @param {Object} metadata - Metadati da salvare
 */
const saveCacheMetadata = (metadata) => {
  try {
    localStorage.setItem(META_CACHE_KEY, JSON.stringify({
      ...metadata,
      version: CACHE_VERSION
    }));
  } catch (error) {
    console.error('Errore nel salvataggio metadati cache:', error);
  }
};

/**
 * Genera una chiave cache basata su parametri
 * @param {string} datasetId - ID del dataset
 * @param {Object} filters - Filtri applicati
 * @returns {string} Chiave univoca
 */
const generateCacheKey = (datasetId, filters = {}) => {
  // Estrai e ordina i filtri per avere chiavi consistenti
  const { anno, trimestre, cdc, vds, ...otherFilters } = filters;
  const sortedFilters = { anno, trimestre, cdc, vds, ...otherFilters };
  
  // Crea stringa serializzata dei filtri (solo valori presenti)
  const filtersStr = Object.entries(sortedFilters)
    .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}:${value}`)
    .join('-');
  
  return `${CACHE_PREFIX}${datasetId}${filtersStr ? '-' + filtersStr : ''}`;
};

/**
 * Salva un dataset in cache
 * @param {string} datasetId - ID del dataset
 * @param {Object} data - Dati del dataset
 * @param {Object} filters - Filtri applicati
 * @param {number} maxAge - Durata massima in ms
 * @returns {Promise<boolean>} Successo operazione
 */
const cacheDataset = async (datasetId, data, filters = {}, maxAge = CACHE_MAX_AGE_MS) => {
  try {
    await initIndexedDB();
    
    const cacheKey = generateCacheKey(datasetId, filters);
    const expiresAt = Date.now() + maxAge;
    
    // Prepara oggetto cache con metadati
    const cacheObject = {
      cacheKey,
      datasetId,
      data,
      filters,
      timestamp: Date.now(),
      expiresAt,
      version: CACHE_VERSION
    };
    
    // Salva in IndexedDB
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.put(cacheObject);
      
      request.onsuccess = () => {
        // Aggiorna anche i metadati
        const metadata = getCacheMetadata();
        metadata.entries[cacheKey] = {
          datasetId,
          filters,
          timestamp: Date.now(),
          expiresAt
        };
        saveCacheMetadata(metadata);
        
        console.log(`💾 Dataset ${datasetId} salvato in cache. Scade: ${new Date(expiresAt).toLocaleString()}`);
        resolve(true);
      };
      
      request.onerror = (event) => {
        console.error('Errore nel salvataggio in cache:', event);
        reject(new Error('Errore nel salvataggio in cache'));
      };
    });
    
  } catch (error) {
    console.error('Errore cacheDataset:', error);
    return false;
  }
};

/**
 * Recupera un dataset dalla cache
 * @param {string} datasetId - ID del dataset
 * @param {Object} filters - Filtri da applicare
 * @returns {Promise<Object|null>} Dataset o null se non in cache
 */
const getDatasetFromCache = async (datasetId, filters = {}) => {
  try {
    await initIndexedDB();
    
    const cacheKey = generateCacheKey(datasetId, filters);
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.get(cacheKey);
      
      request.onsuccess = (event) => {
        const result = event.target.result;
        
        if (!result) {
          console.log(`❌ Dataset ${datasetId} non trovato in cache`);
          return resolve(null);
        }
        
        // Controlla se è scaduto
        if (result.expiresAt < Date.now()) {
          console.log(`⏰ Cache per dataset ${datasetId} scaduta`);
          
          // Pianifica pulizia asincrona
          setTimeout(() => invalidateDatasetCache(datasetId, filters), 0);
          
          return resolve(null);
        }
        
        console.log(`✅ Dataset ${datasetId} recuperato da cache`);
        resolve(result.data);
      };
      
      request.onerror = (event) => {
        console.error('Errore nel recupero dalla cache:', event);
        reject(new Error('Errore nel recupero dalla cache'));
      };
    });
    
  } catch (error) {
    console.error('Errore getDatasetFromCache:', error);
    return null;
  }
};

/**
 * Invalida la cache per un dataset specifico
 * @param {string} datasetId - ID del dataset
 * @param {Object} filters - Filtri specifici (opzionale)
 * @returns {Promise<boolean>} Successo operazione
 */
const invalidateDatasetCache = async (datasetId, filters = null) => {
  try {
    await initIndexedDB();
    
    // Se filters è specificato, invalida solo quella cache specifica
    if (filters) {
      const cacheKey = generateCacheKey(datasetId, filters);
      
      return new Promise((resolve) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        
        const request = store.delete(cacheKey);
        
        request.onsuccess = () => {
          // Aggiorna anche i metadati
          const metadata = getCacheMetadata();
          delete metadata.entries[cacheKey];
          saveCacheMetadata(metadata);
          
          console.log(`🗑️ Cache specifica per dataset ${datasetId} invalidata`);
          resolve(true);
        };
        
        request.onerror = () => {
          console.error('Errore nell\'invalidazione cache');
          resolve(false);
        };
      });
    }
    
    // Altrimenti, invalida tutte le cache per quel dataset
    const metadata = getCacheMetadata();
    const keys = Object.keys(metadata.entries)
      .filter(key => key.startsWith(`${CACHE_PREFIX}${datasetId}`));
    
    if (keys.length === 0) return true;
    
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    return Promise.all(keys.map(key => {
      return new Promise((resolve) => {
        const request = store.delete(key);
        
        request.onsuccess = () => {
          delete metadata.entries[key];
          resolve(true);
        };
        
        request.onerror = () => {
          console.error(`Errore nell'invalidazione di ${key}`);
          resolve(false);
        };
      });
    }))
    .then(() => {
      saveCacheMetadata(metadata);
      console.log(`🗑️ Tutte le cache per dataset ${datasetId} invalidate`);
      return true;
    });
    
  } catch (error) {
    console.error('Errore invalidateDatasetCache:', error);
    return false;
  }
};

/**
 * Pulisce le cache scadute
 * @returns {Promise<number>} Numero di elementi rimossi
 */
const cleanupExpiredCache = async () => {
  try {
    await initIndexedDB();
    
    const metadata = getCacheMetadata();
    const now = Date.now();
    
    // Se l'ultima pulizia è recente, salta
    if (metadata.lastCleanup && (now - metadata.lastCleanup < 3600000)) { // 1 ora
      return 0;
    }
    
    // Identifica chiavi scadute
    const expiredKeys = Object.entries(metadata.entries)
      .filter(([_, entry]) => entry.expiresAt < now)
      .map(([key]) => key);
    
    if (expiredKeys.length === 0) {
      metadata.lastCleanup = now;
      saveCacheMetadata(metadata);
      return 0;
    }
    
    // Rimuovi elementi scaduti
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    await Promise.all(expiredKeys.map(key => {
      return new Promise((resolve) => {
        const request = store.delete(key);
        request.onsuccess = () => {
          delete metadata.entries[key];
          resolve();
        };
        request.onerror = resolve; // Continua anche in caso di errori
      });
    }));
    
    // Aggiorna metadati
    metadata.lastCleanup = now;
    saveCacheMetadata(metadata);
    
    console.log(`🧹 Pulizia cache completata: ${expiredKeys.length} elementi rimossi`);
    return expiredKeys.length;
    
  } catch (error) {
    console.error('Errore cleanupExpiredCache:', error);
    return 0;
  }
};

/**
 * Ottiene le statistiche sulla cache
 * @returns {Object} Statistiche della cache
 */
const getCacheStats = () => {
  try {
    const metadata = getCacheMetadata();
    const now = Date.now();
    
    const entries = Object.values(metadata.entries);
    const total = entries.length;
    const expired = entries.filter(entry => entry.expiresAt < now).length;
    const valid = total - expired;
    
    const datasets = new Set(
      entries.map(entry => entry.datasetId)
    ).size;
    
    const oldestEntry = entries.length > 0
      ? Math.min(...entries.map(entry => entry.timestamp))
      : null;
    
    const newestEntry = entries.length > 0
      ? Math.max(...entries.map(entry => entry.timestamp))
      : null;
    
    return {
      version: metadata.version || CACHE_VERSION,
      total,
      valid,
      expired,
      datasets,
      oldestEntry,
      newestEntry,
      lastCleanup: metadata.lastCleanup
    };
    
  } catch (error) {
    console.error('Errore getCacheStats:', error);
    return {
      version: CACHE_VERSION,
      total: 0,
      valid: 0,
      expired: 0,
      datasets: 0,
      oldestEntry: null,
      newestEntry: null,
      lastCleanup: null
    };
  }
};

/**
 * Recupera un dataset dal backend usando il nuovo endpoint unificato
 * @param {string} datasetId - ID del dataset
 * @param {Object} filters - Filtri da applicare
 * @param {boolean} useCache - Se usare la cache
 * @returns {Promise<Object>} Dati del report
 */
const fetchDatasetFromBackend = async (datasetId, filters = {}, useCache = true) => {
  try {
    // 1. Prima prova la cache se abilitata
    if (useCache) {
      const cachedData = await getDatasetFromCache(datasetId, filters);
      if (cachedData) return cachedData;
    }
    
    // 2. Se non in cache o cache disabilitata, chiama il backend
    console.log(`📡 Recupero dataset ${datasetId} dal backend...`);
    
    // Usa il nuovo endpoint unificato con API Firebase v9
    const getCompleteDataset = httpsCallable(functions, 'getCompleteDataset');
    
    const startTime = performance.now();
    // Inverti l'ordine per assicurarti che datasetId non venga sovrascritto dai filtri
    const response = await getCompleteDataset({
      ...filters,
      datasetId // Mantieni datasetId come ultima proprietà per evitare sovrascritture
    });
    const endTime = performance.now();
    
    // Se c'è un errore dal backend
    if (!response.data.success) {
      throw new Error(response.data.error || 'Errore nel recupero dataset');
    }
    
    console.log(`✅ Dataset recuperato in ${Math.round(endTime - startTime)}ms`);
    
    // 3. Salva in cache per usi futuri se il recupero è riuscito
    if (useCache) {
      // La cache viene salvata in modo asincrono
      cacheDataset(datasetId, response.data, filters)
        .catch(err => console.error('Errore nel salvataggio cache:', err));
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Errore fetchDatasetFromBackend:', error);
    throw error;
  }
};

/**
 * Inizializza il servizio di cache
 */
const initCacheService = async () => {
  try {
    // Inizializza IndexedDB
    await initIndexedDB();
    
    // Controlla versione cache e pulisci se obsoleta
    const metadata = getCacheMetadata();
    if (metadata.version !== CACHE_VERSION) {
      console.log(`🔄 Aggiornamento cache da ${metadata.version || 'sconosciuta'} a ${CACHE_VERSION}`);
      
      // Reset completo per cambi versione
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.clear();
      
      // Resetta metadati
      saveCacheMetadata({ 
        version: CACHE_VERSION, 
        entries: {}, 
        lastCleanup: Date.now() 
      });
    }
    
    // Pianifica pulizia iniziale
    setTimeout(() => cleanupExpiredCache(), 2000);
    
    console.log('🚀 Servizio cache inizializzato');
    return true;
    
  } catch (error) {
    console.error('Errore initCacheService:', error);
    return false;
  }
};

// Esporta le funzioni del servizio
const cacheService = {
  initCacheService,
  cacheDataset,
  getDatasetFromCache,
  invalidateDatasetCache,
  cleanupExpiredCache,
  getCacheStats,
  fetchDatasetFromBackend,
  generateCacheKey
};

export default cacheService;
